import { ensureAnalyticsAvailable } from 'src/lib/analytics-utils';
import { isProduction } from 'src/lib/env-constants';
import { isPaidTraffic } from 'src/lib/is-us-organic-form-traffic';

import trackLinkedIn, { linkedInConversionIds } from './trackLinkedIn';
import trackTTD, { TTDConversionIds } from './trackTTD';
import { getDefaultParameters } from './utilities';

const isPingdomVisit = () => {
  return (
    new URLSearchParams(window.location.search).get('ignore') === 'pingdom'
  );
};

const getDefaultContext = () => {
  return {
    // Segment normally tracks IP addresses. We don't care to persist
    // these, so we redact entirely by overriding `context.ip`.
    ip: '0.0.0.0',
  };
};

export const trackingUtils = () => {
  if (!ensureAnalyticsAvailable()) {
    return;
  }

  window.analytics.ready(() => {
    // Wait max 500ms for LinkedIn network request to return conversion pixel
    // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#extending-timeout
    window.analytics.timeout(500);

    // Emitter event listener to run custom code,
    // useful for sending data to 3rd party service not integrated w/ Segment
    // i.e. LinkedIn conversion pixel request
    // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#emitter
    // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#managing-data-flow-with-the-integrations-object
    window.analytics.on(
      'track',
      (event, _, { integrations = {}, consentRequired = false } = {}) => {
        const shouldTrackLinkedIn =
          Object.keys(linkedInConversionIds).includes(event) &&
          (integrations['LinkedIn Insight Tag'] || !consentRequired);

        const shouldTrackTTD =
          Object.keys(TTDConversionIds).includes(event) &&
          (integrations['TTD'] || !consentRequired);

        if (shouldTrackLinkedIn) {
          trackLinkedIn({
            conversionId: linkedInConversionIds[event],
          });
        }

        if (shouldTrackTTD) {
          trackTTD({ conversionId: TTDConversionIds[event] });
        }
      },
    );
  });
};

/**
 * Thin implementation wrapper around Segment's `track` method that bakes
 * in some defaults. `analytics.track` should never be called directly.
 */
export const trackEvent = ({
  event,
  payload = {},
  callback = () => {
    return undefined;
  },
}: {
  event: string;
  payload?: Record<string, unknown>;
  callback?: () => unknown;
}) => {
  if (!ensureAnalyticsAvailable()) {
    return;
  }

  if (isPingdomVisit()) {
    callback();
    return;
  }

  // Normalize event name to SNAKE_CASE
  const normalizedEvent = event.replace(/ /g, '_').toUpperCase();

  if (!isProduction) {
    // eslint-disable-next-line no-console
    console.log('ANALYTICS EVENT: ', normalizedEvent, payload);
  }

  // eslint-disable-next-line no-restricted-syntax
  window.analytics.track(
    normalizedEvent,
    {
      ...getDefaultParameters(),
      ...payload,
    },
    {
      context: getDefaultContext(),
      // @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#managing-data-flow-with-the-integrations-object
      integrations: window?.analytics?.integrations || {},
    },
    callback,
  );
};

export const trackPageView = ({
  payload = {},
}: {
  payload?: Record<string, unknown>;
} = {}) => {
  if (!ensureAnalyticsAvailable() || isPingdomVisit()) {
    return;
  }

  // eslint-disable-next-line no-restricted-syntax
  window.analytics.page(
    'page-view',
    'Page',
    {
      ...getDefaultParameters(),
      ...payload,
      // Last-touch paid acquisition, same helper as experiment eligibility.
      isPaid: isPaidTraffic(),
    },
    {
      context: getDefaultContext(),
    },
  );
};
